import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { authenticate, usePasskeySignInOptionsMutation, usePasskeySignInStatusMutation } from '@common-reducers/backend-api/backend-api';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { clearAuthError } from '@common-reducers/UserReducer';
import { namespace } from '@common-config/constants';
import { isExtensionContext } from '@common-services/detect-context';

type Notice = 'cancelled' | 'rateLimited' | 'failed' | null;

/** The browser raises NotAllowedError/AbortError when the person dismisses the prompt. */
const isCancellation = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError');

/**
 * "Sign in with a passkey" (#545 slice b). Renders nothing in the extension (its origin cannot assert
 * for the web relying party), in browsers without WebAuthn, or while the backend flag is off, so the
 * password / Google form is untouched. A failed attempt shows an inline notice and leaves the form usable.
 */
export const PasskeySignInButton = () => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [status] = usePasskeySignInStatusMutation();
  const [signInOptions] = usePasskeySignInOptionsMutation();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    if (isExtensionContext() || !browserSupportsWebAuthn()) return;
    let cancelled = false;
    status().unwrap().then(
      (result) => { if (!cancelled) setEnabled(result.ok && result.enabled); },
      () => { if (!cancelled) setEnabled(false); },
    );
    return () => { cancelled = true; };
  }, [status]);

  if (!enabled) return null;

  const signIn = async () => {
    setBusy(true);
    setNotice(null);
    dispatch(clearAuthError());
    try {
      const options = await signInOptions().unwrap();
      if (!options.ok) {
        setNotice(options.reason === 'rate-limited' ? 'rateLimited' : 'failed');
        return;
      }
      const response = await startAuthentication({ optionsJSON: options.options });
      await dispatch(authenticate.initiate({ strategy: 'passkey', challengeId: options.challengeId, response })).unwrap();
    } catch (error) {
      setNotice(isCancellation(error) ? 'cancelled' : 'failed');
    } finally {
      setBusy(false);
    }
  };

  const noticeText = notice === 'cancelled' ? t('loginForm.passkeyCancelled')
    : notice === 'rateLimited' ? t('loginForm.passkeyRateLimited')
      : notice === 'failed' ? t('loginForm.passkeyFailed') : null;

  return (
    <div className="space-y-2" data-testid="passkey-signin">
      <button
        type="button"
        data-testid="passkey-signin-button"
        onClick={signIn}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 rounded-md border text-sm font-medium h-10 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--color-border, #e5e7eb)', backgroundColor: 'var(--color-surface, #ffffff)', color: 'var(--color-text, #111827)' }}
      >
        <KeyRound className="h-4 w-4" />
        {busy ? t('loginForm.passkeySigningIn') : t('loginForm.passkeySignIn')}
      </button>
      {noticeText && <p role="alert" data-testid="passkey-signin-notice" className="text-sm text-red-500">{noticeText}</p>}
    </div>
  );
};
